/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ButtonV2 from 'components/ButtonV2';
import {
  PageShell,
  PageHeader,
  DataTable,
  Icon,
  StatStrip,
  StatTile,
  ActionIcon,
  SegmentedFilter,
  Money,
  Num,
  Split,
  Strong,
  useIsMobile,
} from 'components/v3';
import { downloadCsv } from 'shared/csv';
import { formatCurrency } from 'shared/formatCurrency';
import { HELP } from 'shared/profitHelp';
import {
  ledgerPeriodOptions,
  periodYear,
  YEAR_TO_DATE,
} from 'shared/reportPeriods';
import { useGetRevenueByAccount } from './gql/useGetRevenueByAccount';

// Admin Financial > Revenue: every account with ledger activity in the
// selected year, with sales, platform fee and the house profit estimate, plus
// a totals row. The on-screen version of the net-by-account PDFs.

const COLUMNS = [
  { key: 'name', label: 'Account', width: 'minmax(220px,2fr)', primary: true },
  { key: 'events_held', label: 'Events', width: '80px', align: 'right' },
  { key: 'sales', label: 'Sales', width: '110px', align: 'right' },
  { key: 'app_pos', label: 'App / POS', width: '110px', align: 'right' },
  { key: 'cash_card', label: 'Cash / Card', width: '110px', align: 'right' },
  {
    key: 'app_profit',
    label: 'App Profit',
    help: HELP.appProfit,
    width: '110px',
    align: 'right',
  },
  {
    key: 'pos_profit',
    label: 'POS Profit',
    help: HELP.posProfit,
    width: '110px',
    align: 'right',
  },
  {
    key: 'total_profit',
    label: 'Total Profit',
    width: '110px',
    align: 'right',
  },
  {
    key: 'actions',
    label: 'Actions',
    width: '70px',
    align: 'right',
    skeleton: 'actions',
    actionsCount: 1,
  },
];

const MONEY_KEYS = [
  'app_sales',
  'pos_card_sales',
  'pos_cash_sales',
  'platform_fee',
  'app_profit',
  'pos_profit',
  'total_profit',
];

const CSV = [
  { header: 'Account', value: (r) => r.name },
  { header: 'Events', value: (r) => r.events_held },
  { header: 'App Sales', value: (r) => (r.app_sales || 0).toFixed(2) },
  { header: 'POS Card', value: (r) => (r.pos_card_sales || 0).toFixed(2) },
  { header: 'POS Cash', value: (r) => (r.pos_cash_sales || 0).toFixed(2) },
  { header: 'Platform Fee', value: (r) => (r.platform_fee || 0).toFixed(2) },
  { header: 'App Profit', value: (r) => (r.app_profit || 0).toFixed(2) },
  { header: 'POS Profit', value: (r) => (r.pos_profit || 0).toFixed(2) },
  { header: 'Total Profit', value: (r) => (r.total_profit || 0).toFixed(2) },
];

// Sum every account row for the KPI tiles.
const totalsOf = (rows) => {
  const total = { events_held: 0 };
  MONEY_KEYS.forEach((k) => {
    total[k] = 0;
  });
  rows.forEach((r) => {
    total.events_held += r.events_held || 0;
    MONEY_KEYS.forEach((k) => {
      total[k] += r[k] || 0;
    });
  });
  return total;
};

export default function RevenuePage() {
  const isMobile = useIsMobile();
  const history = useHistory();
  const [period, setPeriod] = useState(YEAR_TO_DATE);

  const currentYear = new Date().getFullYear();
  const periodOptions = useMemo(
    () => ledgerPeriodOptions(currentYear),
    [currentYear]
  );
  const year = periodYear(period, currentYear);

  const { data, loading } = useGetRevenueByAccount(year);
  const accounts = data?.getRevenueByAccountV2 || [];
  const totals = useMemo(() => totalsOf(accounts), [accounts]);

  const renderRow = (row) => ({
    name: <Strong>{row.name}</Strong>,
    events_held: <Num value={row.events_held} />,
    sales: (
      <Money
        value={
          (row.app_sales || 0) +
          (row.pos_card_sales || 0) +
          (row.pos_cash_sales || 0)
        }
      />
    ),
    app_pos: (
      <Split
        a={row.app_sales}
        b={(row.pos_card_sales || 0) + (row.pos_cash_sales || 0)}
      />
    ),
    cash_card: <Split a={row.pos_cash_sales} b={row.pos_card_sales} />,
    app_profit: <Money value={row.app_profit} strong />,
    pos_profit: <Money value={row.pos_profit} strong />,
    total_profit: <Money value={row.total_profit} strong />,
    actions: (
      <ActionIcon
        icon="view"
        title="View account report"
        onClick={() =>
          history.push(`/admin/accounts/reports/${row.account_id}`)
        }
      />
    ),
  });

  return (
    <PageShell>
      <PageHeader icon="circle-dollar-sign" title="Revenue">
        <SegmentedFilter
          value={period}
          onChange={setPeriod}
          options={periodOptions}
        />
      </PageHeader>

      <StatStrip style={{ marginBottom: isMobile ? 16 : 24 }}>
        <StatTile
          label="Total Profit"
          value={formatCurrency(totals.total_profit)}
          loading={loading}
          accent
        />
        <StatTile
          label="App Profit"
          value={formatCurrency(totals.app_profit)}
          loading={loading}
        />
        <StatTile
          label="POS Profit"
          value={formatCurrency(totals.pos_profit)}
          loading={loading}
        />
        <StatTile
          label="Platform Fee"
          value={formatCurrency(totals.platform_fee)}
          loading={loading}
        />
        <StatTile label="Accounts" value={accounts.length} loading={loading} />
      </StatStrip>

      <DataTable
        columns={COLUMNS}
        data={accounts}
        renderRow={renderRow}
        keyField="account_id"
        loading={loading}
        emptyLabel="No sales recorded for this period."
        title="Revenue by Account"
        help={HELP.revenueByAccount}
        action={
          <ButtonV2
            icon={<Icon name="download" size={16} />}
            disabled={loading || !accounts.length}
            onClick={() =>
              downloadCsv(`revenue-by-account-${year}.csv`, CSV, accounts)
            }
            style={{ width: 'auto' }}
          >
            Export to CSV
          </ButtonV2>
        }
      />
    </PageShell>
  );
}
